import { LearningSessionsApi, MeApi } from 'src/api';
import { convertDateStringToUnixTimestamp, getNowAsUnixTimestamp } from 'src/utils/dateTime';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import {
  ApiSessionSourceType,
  CompactLearnableProgress,
  CompactLearnableWithProgress,
  Correctness,
  Learnable,
  LearnableProgress,
  LearnableWithProgress,
  LearningEvent,
  ProgressPreferences,
  SessionSourceType,
  SessionType,
  SummaryItem,
  TemplateName,
} from 'src/api/learningSessions/types';
import { getPresentationCardData } from '../Common/learnableCards/utils';

type CompactLearningEvent = Omit<LearningEvent, 'notDifficult' | 'ignored' | 'starred'>;

interface TestAnsweredExtraInfo {
  correctness: Correctness;
  givenAnswer: string;
  oldLearnableWithProgress: LearnableWithProgress;
  testId: string;
  templateName: TemplateName;
}

export const makeLearnablesWithProgress = (
  learnables: Learnable[],
  progressArr: LearnableProgress[],
): LearnableWithProgress[] => {
  if (learnables.length < 1) return [];

  const learnableIdProgressMap: {
    [learnableId: string]: LearnableProgress;
  } = progressArr.reduce(
    (accumulator, currentProgressItem) => ({
      ...accumulator,
      [currentProgressItem.learnableId]: currentProgressItem,
    }),
    {},
  );

  return learnables.map(learnable => ({
    learnable,
    progress: learnableIdProgressMap[learnable.id] || null,
  }));
};

export const convertSessionDataToSummary = (
  seenLearnablesWithUpdatedProgress: {
    [id: string]: CompactLearnableWithProgress;
  },
  learnablesWithProgressLookup: { [id: string]: LearnableWithProgress } = {},
): SummaryItem[] => {
  return Object.values(seenLearnablesWithUpdatedProgress).map(
    (l: CompactLearnableWithProgress): SummaryItem => {
      const presentationCardData = getPresentationCardData(l.learnable);
      return {
        item: presentationCardData.item,
        definition: presentationCardData.definition,
        growthLevel: l.progress.growthLevel,
        learnableId: l.learnable.id,
        growthLevelBefore: learnablesWithProgressLookup[l.learnable.id]?.progress?.growthLevel ?? 0,
        nextReviewDate: l.progress.nextDate,
      };
    },
  );
};

export const generateLearningEvent = ({
  sessionSourceId,
  sessionSourceType,
  answeredExtraInfo,
  updatedProgress,
  points,
  msTimeSpent,
}: {
  sessionSourceId: number;
  sessionSourceType: SessionSourceType;
  answeredExtraInfo: TestAnsweredExtraInfo;
  updatedProgress: CompactLearnableProgress;
  points: number;
  msTimeSpent: number;
}): CompactLearningEvent => {
  const { learnable, progress: oldProgress } = answeredExtraInfo.oldLearnableWithProgress;

  const nowInSeconds = getNowAsUnixTimestamp();

  const isLanguagePair = sessionSourceType === 'language_pair';
  const isScenario = sessionSourceType === 'scenario';

  return {
    learnableId: updatedProgress.learnableId,
    languagePairId: isLanguagePair ? sessionSourceId : undefined,
    scenarioId: isScenario ? sessionSourceId : undefined,
    learningElement: learnable.learningElement,
    definitionElement: learnable.definitionElement,
    boxTemplate: answeredExtraInfo.templateName,
    givenAnswer: answeredExtraInfo.givenAnswer,

    timeSpent: msTimeSpent,
    when: nowInSeconds,
    lastDate: nowInSeconds,
    testId: answeredExtraInfo.testId,

    /** These come directly from the updated progress */
    correct: updatedProgress.correct,
    attempts: updatedProgress.attempts,
    totalStreak: updatedProgress.totalStreak,
    currentStreak: updatedProgress.currentStreak,
    growthLevel: updatedProgress.growthLevel,
    interval: updatedProgress.interval || null,
    points,
    bonusPoints: 0,
    score: answeredExtraInfo.correctness === 'Correct' ? 1.0 : 0,
    memId: oldProgress?.memId || null,

    nextDate: updatedProgress.nextDate
      ? convertDateStringToUnixTimestamp(updatedProgress.nextDate)
      : null,
    createdDate: convertDateStringToUnixTimestamp(updatedProgress.createdDate),
  };
};

const POST_BATCH_SIZE = 50;

export const batchSendLearningEvents = async (
  unsentLearningEvents: CompactLearningEvent[],
  progressPreferences: { [key: number]: ProgressPreferences },
  sessionType: SessionType,
  sessionSourceType: ApiSessionSourceType,
  sessionSourceId: number,
  sessionId: string,
) => {
  const languagePairId = getLanguagePairId();
  // Safe copy so splice doesn't modify the argument
  const safeUnsentLearningEvents = [...unsentLearningEvents];

  while (safeUnsentLearningEvents.length > 0) {
    // Remove the first batch of events and send
    const compactBatchEvents = safeUnsentLearningEvents.splice(0, POST_BATCH_SIZE);
    const completeBatchEvents = compactBatchEvents.map(event => ({
      ...event,
      // User can change these values during the session
      // We don't know the final state of these until the end of the session
      notDifficult: progressPreferences[event.learnableId].notDifficult,
      starred: progressPreferences[event.learnableId].starred,
      ignored: progressPreferences[event.learnableId].ignored,
    }));
    const convertedSessionType = ['classic_review'].includes(sessionType)
      ? ('review' as SessionType.REVIEW_ALEX)
      : sessionType;

    if (!languagePairId) return;
    await MeApi.postProgressRegister(
      languagePairId,
      completeBatchEvents,
      sessionId,
      convertedSessionType,
      sessionSourceType,
      sessionSourceId,
    );
  }
};

interface WithPoints {
  points: number;
}

export const getPoints = <T extends WithPoints>(events: T[]): number =>
  events.reduce((total, { points }) => total + points, 0);

export const adjustProgressPreferences = (
  sessionType: SessionType,
  progressPreferences: { [learnableId: number]: ProgressPreferences },
) => {
  if (sessionType !== SessionType.DIFFICULT_WORDS) {
    return progressPreferences;
  }

  const adjustedProgressPreferences = { ...progressPreferences };
  // Remove manually tagged difficult words after a successful difficult words session
  Object.keys(adjustedProgressPreferences).forEach(learnableId => {
    adjustedProgressPreferences[parseInt(learnableId)].starred = false;
  });

  return adjustedProgressPreferences;
};

export const batchSendLearningEventsAtEndOfSession = async (
  learningEvents: CompactLearningEvent[],
  analyticsSessionId: string,
  sessionType: SessionType,
  sessionSourceType: ApiSessionSourceType,
  sessionSourceId: number,
  progressPreferences: { [learnableId: number]: ProgressPreferences },
  sessionEndReason: 'completed' | 'exited',
  sessionLearnableIds: number[],
) => {
  const unsentLearningEvents = learningEvents.slice();
  // Unsent learning events are removed as sent, so need to calculate points first
  const points = getPoints(unsentLearningEvents);

  const adjustedProgressPreferences = adjustProgressPreferences(sessionType, progressPreferences);

  await batchSendLearningEvents(
    unsentLearningEvents,
    adjustedProgressPreferences,
    sessionType,
    sessionSourceType,
    sessionSourceId,
    analyticsSessionId,
  );

  const endOfSessionResponse = await LearningSessionsApi.end({
    sessionId: analyticsSessionId,
    sessionType,
    sessionSourceType,
    sessionPoints: points,
    sessionSourceId,
    endReason: sessionEndReason,
    allSessionLearnablesIdKeys: sessionLearnableIds,
  });

  return endOfSessionResponse;
};

interface WithScore {
  /** Float representing the correctness of the answer */
  score: number;
}

// Note that we assume 1.0 is correct as this works fine for
// speed review but this will change for other sessions (i.e.
// tapping and typing) which have fuzzier correctness.
// Update here or use memlearning accordingly
const isCorrect = (score: number) => score === 1;

/**
 * Calculates the absolute number of correct answers from an
 * array of learning events.
 */
export const getNumCorrectAnswers = <T extends WithScore>(events: T[]): number =>
  events.reduce((total, { score }) => (isCorrect(score) ? total + 1 : total), 0);

export const addBonusPointsToLearningEvents = <T extends WithPoints>(
  bonusPoints: number,
  learningEvents: T[],
) => [
  ...learningEvents.slice(0, learningEvents.length - 1),
  {
    ...learningEvents[learningEvents.length - 1],
    points: learningEvents[learningEvents.length - 1].points + bonusPoints,
  },
];

export const getAccuracy = (attempts: number, correct: number) =>
  attempts === 0 ? 0 : parseFloat((correct / attempts).toFixed(2));
